import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useCollection } from "../collection/CollectionContext";
import { useCollectionView } from "../collection/CollectionViewContext";
import { useCompactShell } from "../layouts/useCompactShell";
import { CollectionSearchField, CollectionFilters } from "../collection/CollectionToolbar";
import { formatProgressLabel } from "../collection/collection-progress";
import { Button } from "../components/Button";
import { ErrorMessage } from "../components/ErrorMessage";
import { LoadingIndicator } from "../components/LoadingIndicator";
import { ReleaseCard } from "../components/ReleaseCard";
import { useGridDensity } from "../ui/GridDensityContext";
import { GridDensityToggle } from "../ui/GridDensityToggle";

function emptyMessage(kind: ReturnType<typeof useCollectionView>["emptyKind"]): string {
  switch (kind) {
    case "collection":
      return "В коллекции пока пусто.";
    case "search":
      return "По поиску ничего не найдено.";
    case "series":
      return "В этой серии у вас пока нет машинок.";
    case "rarity":
      return "С этой редкостью у вас пока нет машинок.";
    case "year":
      return "В этом году у вас пока нет машинок.";
    case "filters":
      return "По выбранным фильтрам ничего нет.";
    default:
      return "";
  }
}

export function CollectionPage() {
  const collection = useCollection();
  const view = useCollectionView();
  const { density } = useGridDensity();
  const compactShell = useCompactShell();

  useEffect(() => {
    void view.ensureProgressMeta();
  }, [view.ensureProgressMeta]);

  const progressLabel = formatProgressLabel(view.progress);
  const visibleItems = view.visibleItems;

  return (
    <section className="hw-page hw-page--collection">
      <h1 className="hw-visually-hidden">Коллекция</h1>

      {collection.status === "ready" && !compactShell ? (
        <div className="hw-collection-toolbar">
          <div className="hw-collection-toolbar__actions">
            <GridDensityToggle />
          </div>
        </div>
      ) : null}

      {collection.status === "ready" && compactShell ? (
        <div className="hw-collection-controls">
          <CollectionSearchField className="hw-collection-controls__search" />
          <div className="hw-collection-controls__actions">
            <GridDensityToggle />
          </div>
        </div>
      ) : null}

      <CollectionFilters />

      {collection.status === "ready" ? (
        <p className="hw-collection-counts" aria-live="polite">
          {progressLabel.detail}
        </p>
      ) : null}

      {collection.stale ? (
        <p className="hw-status-banner" role="status">
          Показана сохранённая копия. Изменения недоступны без сети.
        </p>
      ) : null}
      {!collection.online && !collection.stale ? (
        <p className="hw-status-banner" role="status">
          Нет сети. Изменения коллекции недоступны.
        </p>
      ) : null}

      {collection.conflictMessage ? (
        <ErrorMessage>{collection.conflictMessage}</ErrorMessage>
      ) : null}
      {collection.errorMessage ? (
        <ErrorMessage>{collection.errorMessage}</ErrorMessage>
      ) : null}

      {collection.status === "loading" || collection.status === "idle" ? (
        <LoadingIndicator label="Загрузка коллекции…" />
      ) : null}

      {collection.status === "error" ? (
        <div className="hw-stack">
          <ErrorMessage>
            {collection.errorMessage ?? "Не удалось загрузить коллекцию."}
          </ErrorMessage>
          <Button
            type="button"
            onClick={() => {
              void collection.refresh();
            }}
          >
            Повторить
          </Button>
        </div>
      ) : null}

      {collection.status === "ready" && view.emptyKind === "collection" ? (
        <div className="hw-empty hw-stack">
          <p role="status">{emptyMessage("collection")}</p>
          <Link className="hw-text-link" to="/catalog">
            Перейти в каталог
          </Link>
        </div>
      ) : null}

      {collection.status === "ready" &&
      view.emptyKind !== "none" &&
      view.emptyKind !== "collection" ? (
        <p className="hw-empty" role="status">
          {emptyMessage(view.emptyKind)}
        </p>
      ) : null}

      {collection.status === "ready" && visibleItems.length > 0 ? (
        <ul className={`hw-release-grid hw-release-grid--collection hw-release-grid--cols-${density}`}>
          {visibleItems.map((item, index) => (
            <li key={item.releaseId}>
              <ReleaseCard
                release={item.release}
                variant="collection"
                priorityImage={index < 4}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}
